/**
 * Pinned pages (favourites).
 *
 * A pin is a page or a saved view: the path keeps its query string, so a
 * filtered Signals view comes back with its filters. Pins are per-browser —
 * there is no user-preferences API yet — and capped at MAX_PINS.
 *
 * Every instance of the hook stays in step: a change in this tab is announced
 * with PINS_EVENT, and a change in another tab arrives as a `storage` event.
 * Storage can be unavailable (private mode, blocked site data, quota), so every
 * read and write is guarded and the module keeps an in-memory copy to fall
 * back on; pins then last for the session instead of breaking the sidebar.
 */
import { useCallback, useEffect, useState } from 'react';

export const PINS_STORAGE_KEY = 'saf3ai.pins';
export const PINS_EVENT = 'saf3ai:pins-changed';
export const MAX_PINS = 10;

let memoryPins = [];

/** pathname + search, without the origin or hash, and without a trailing slash. */
export const normalizePinPath = (raw) => {
  if (raw == null) return '';
  let value = String(raw).trim();
  if (!value) return '';
  try {
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(value)) {
      const url = new URL(value);
      value = `${url.pathname}${url.search}`;
    }
  } catch {
    return '';
  }
  const hashAt = value.indexOf('#');
  if (hashAt >= 0) value = value.slice(0, hashAt);
  const queryAt = value.indexOf('?');
  let pathname = queryAt >= 0 ? value.slice(0, queryAt) : value;
  let search = queryAt >= 0 ? value.slice(queryAt) : '';
  if (!pathname.startsWith('/')) pathname = `/${pathname}`;
  if (pathname.length > 1) pathname = pathname.replace(/\/+$/, '') || '/';
  if (search === '?') search = '';
  return `${pathname}${search}`;
};

const sanitize = (p) => {
  if (!p || typeof p !== 'object') return null;
  const path = normalizePinPath(p.path);
  if (!path || typeof p.id !== 'string' || !p.id) return null;
  const label = String(p.label ?? '').trim() || path;
  return {
    id: p.id,
    path,
    label,
    module: p.module ? String(p.module) : '',
    createdAt: p.createdAt ? String(p.createdAt) : '',
  };
};

/** Current pins from storage (or the in-memory copy when storage is unavailable). */
export const readPins = () => {
  let raw;
  try {
    raw = window.localStorage.getItem(PINS_STORAGE_KEY);
  } catch {
    return memoryPins.slice();
  }
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const seen = new Set();
    return parsed
      .map(sanitize)
      .filter((p) => {
        if (!p || seen.has(p.id)) return false;
        seen.add(p.id);
        return true;
      })
      .slice(0, MAX_PINS);
  } catch {
    return [];
  }
};

const writePins = (pins) => {
  memoryPins = pins.slice();
  try {
    window.localStorage.setItem(PINS_STORAGE_KEY, JSON.stringify(pins));
  } catch {
    // Storage blocked or full — the in-memory copy above carries the session.
  }
  try {
    window.dispatchEvent(new CustomEvent(PINS_EVENT));
  } catch {
    // Very old browsers: other hook instances catch up on their next mount.
  }
};

const newId = () => `pin_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * usePins() → { pins, max, isFull, isPinned(path), findPin(path),
 *               pin({path,label,module}), unpin(id), rename(id,label),
 *               move(id, dir), toggle({path,label,module}) }
 *
 * `pin` returns the created pin, the existing one when the path is already
 * pinned, or null when the list is full. `move` takes 'up' | 'down' (or -1/1).
 */
export default function usePins() {
  const [pins, setPins] = useState(readPins);

  useEffect(() => {
    const sync = () => setPins(readPins());
    const onStorage = (e) => {
      if (!e || e.key === null || e.key === PINS_STORAGE_KEY) sync();
    };
    window.addEventListener(PINS_EVENT, sync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(PINS_EVENT, sync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  // Mutations start from what is stored now, not from this instance's render,
  // so two hook instances can never overwrite each other's change.
  const commit = useCallback((next) => {
    writePins(next);
    setPins(next);
  }, []);

  const findPin = useCallback((path) => {
    const target = normalizePinPath(path);
    return pins.find((p) => p.path === target) || null;
  }, [pins]);

  const isPinned = useCallback((path) => Boolean(findPin(path)), [findPin]);

  const pin = useCallback(({ path, label, module } = {}) => {
    const target = normalizePinPath(path);
    if (!target) return null;
    const current = readPins();
    const existing = current.find((p) => p.path === target);
    if (existing) return existing;
    if (current.length >= MAX_PINS) return null;
    const created = {
      id: newId(),
      path: target,
      label: String(label ?? '').trim() || target,
      module: module ? String(module) : '',
      createdAt: new Date().toISOString(),
    };
    commit([...current, created]);
    return created;
  }, [commit]);

  const unpin = useCallback((id) => {
    const current = readPins();
    const next = current.filter((p) => p.id !== id);
    if (next.length !== current.length) commit(next);
  }, [commit]);

  const rename = useCallback((id, label) => {
    const value = String(label ?? '').trim();
    if (!value) return;
    const current = readPins();
    if (!current.some((p) => p.id === id && p.label !== value)) return;
    commit(current.map((p) => (p.id === id ? { ...p, label: value } : p)));
  }, [commit]);

  const move = useCallback((id, dir) => {
    const step = dir === 'up' || dir === -1 ? -1 : dir === 'down' || dir === 1 ? 1 : 0;
    if (!step) return;
    const current = readPins();
    const from = current.findIndex((p) => p.id === id);
    const to = from + step;
    if (from < 0 || to < 0 || to >= current.length) return;
    const next = current.slice();
    [next[from], next[to]] = [next[to], next[from]];
    commit(next);
  }, [commit]);

  const toggle = useCallback(({ path, label, module } = {}) => {
    const target = normalizePinPath(path);
    const existing = readPins().find((p) => p.path === target);
    if (existing) {
      unpin(existing.id);
      return null;
    }
    return pin({ path: target, label, module });
  }, [pin, unpin]);

  return {
    pins,
    max: MAX_PINS,
    isFull: pins.length >= MAX_PINS,
    isPinned,
    findPin,
    pin,
    unpin,
    rename,
    move,
    toggle,
  };
}

/** Two-letter tile text for the collapsed rail ("Critical signals" -> "CS"). */
export const pinInitials = (label) => {
  const words = String(label || '')
    .split(/[\s·/&,-]+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};
